import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { flashcardActions } from '../../redux/actions/flashcard.actions';
import { flashcardService } from '../../services';
import { ImportCardsModal } from './ImportCardsModal';
import { ImageUpload } from './ImageUpload';

interface RootState {
  flashcard: {
    folders: any[];
    loading: boolean;
  };
}

interface CardData {
  id: string; // local id for drag/drop
  originalId?: number; // backend id when editing
  front: string;
  back: string;
  phonetic?: string;
  audio_url?: string;
  image_url?: string;
  position: number;
}

interface SetEditorSimpleProps {
  onBack: () => void;
  onSuccess?: (setId: number) => void;
  prefilledFolderId?: number | null;
  mode?: 'create' | 'edit';
  editSetId?: number | null;
}

export const SetEditorSimple: React.FC<SetEditorSimpleProps> = ({ onBack, onSuccess, prefilledFolderId, mode = 'create', editSetId }) => {
  const dispatch = useDispatch();
  const { folders = [], sets = [], cards: storeCards = [] } = useSelector((state: any) => state.flashcard);

  const isEditMode = mode === 'edit' && !!editSetId;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
    const [folderId, setFolderId] = useState<number | null>(prefilledFolderId || null);
  const [languageFrom, setLanguageFrom] = useState('English');
  const [languageTo, setLanguageTo] = useState('Vietnamese');
  const [cardList, setCardList] = useState<CardData[]>([
    { id: '1', front: '', back: '', phonetic: '', audio_url: '', image_url: '', position: 0 },
    { id: '2', front: '', back: '', phonetic: '', audio_url: '', image_url: '', position: 1 },
  ]);
  const [draggedCard, setDraggedCard] = useState<string | null>(null);
  const [initializedFromStore, setInitializedFromStore] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [isFetchingPhonetics, setIsFetchingPhonetics] = useState(false);

  useEffect(() => {
    dispatch(flashcardActions.getAllFolders() as any);
    if (isEditMode && editSetId) {
      dispatch(flashcardActions.getAllSets() as any);
      dispatch(flashcardActions.getCardsBySet(editSetId) as any);
    }
  }, [dispatch, isEditMode, editSetId]);

  useEffect(() => {
    if (prefilledFolderId) {
      setFolderId(prefilledFolderId);
    }
  }, [prefilledFolderId]);

  // Populate form when editing once data is available
  useEffect(() => {
    if (!isEditMode || !editSetId || initializedFromStore) return;
    const existingSet = sets.find((s: any) => s.id === editSetId);
    const setCards = storeCards.filter((c: any) => c.set_id === editSetId);
    if (existingSet) {
      setName(existingSet.name || '');
      setDescription(existingSet.description || '');
      setFolderId(existingSet.folder_id || null);
      setLanguageFrom(existingSet.language_from || 'English');
      setLanguageTo(existingSet.language_to || 'Vietnamese');
    }
    if (setCards && setCards.length > 0) {
      const mapped = setCards.map((c: any, idx: number) => ({
        id: `existing-${c.id}`,
        originalId: c.id,
        front: c.front || '',
        back: c.back || '',
        phonetic: c.phonetic || '',
        audio_url: c.audio_url || '',
        image_url: c.image_url || '',
        position: idx,
      }));
      setCardList(mapped);
      setInitializedFromStore(true);
    }
  }, [isEditMode, editSetId, sets, storeCards, initializedFromStore]);

  const fetchPhoneticsForCards = async (cards: CardData[]): Promise<CardData[]> => {
    const updatedCards = await Promise.all(
      cards.map(async (card) => {
        if (!card.front.trim()) return card;
        
        try {
          const result = await flashcardService.lookupWord(card.front.trim(), 'vi');
          if (result?.phonetic) {
            return {
              ...card,
              phonetic: result.phonetic || card.phonetic || '',
              // Không tự động lấy audio từ dictionary nữa
            };
          }
          return card;
        } catch (error) {
          console.error(`Error fetching phonetic for "${card.front}":`, error);
          return card;
        }
      })
    );
    return updatedCards;
  };

  const handleAddCard = () => {
    const newCard: CardData = {
      id: Date.now().toString(),
      front: '',
      back: '',
      phonetic: '',
      audio_url: '',
      image_url: '',
      position: cardList.length,
    };
    setCardList([...cardList, newCard]);
  };

  const handleImportCards = (importedCards: Array<{ front: string; back: string; phonetic?: string; audio_url?: string }>) => {
    const startPosition = cardList.length;
    const newCards = importedCards.map((card, index) => ({
      id: Date.now().toString() + index,
      front: card.front,
      back: card.back,
      phonetic: card.phonetic || '',
      audio_url: card.audio_url || '',
      image_url: '',
      position: startPosition + index,
    }));
    // Append to existing cards instead of replacing
    setCardList([...cardList, ...newCards]);
  };

  const handleRemoveCard = (cardId: string) => {
    if (cardList.length <= 2) {
      alert('Phải có ít nhất 2 thẻ từ!');
      return;
    }
    setCardList(cardList.filter(c => c.id !== cardId));
  };

  const handleCardChange = (cardId: string, field: keyof CardData, value: string) => {
    setCardList(cardList.map(c => c.id === cardId ? { ...c, [field]: value } : c));
  };

  const handleDragStart = (cardId: string) => {
    setDraggedCard(cardId);
  };

  const handleDragOver = (e: React.DragEvent, targetCardId: string) => {
    e.preventDefault();
    if (!draggedCard || draggedCard === targetCardId) return;

    const draggedIndex = cardList.findIndex(c => c.id === draggedCard);
    const targetIndex = cardList.findIndex(c => c.id === targetCardId);

    const newCards = [...cardList];
    const [removed] = newCards.splice(draggedIndex, 1);
    newCards.splice(targetIndex, 0, removed);

    newCards.forEach((card, index) => {
      card.position = index;
    });

    setCardList(newCards);
  };

  const handleDragEnd = () => {
    setDraggedCard(null);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      alert('Vui lòng nhập tên set!');
      return;
    }

    const validCards = cardList.filter(c => c.front.trim() && c.back.trim());
    if (validCards.length < 2) {
      alert('Phải có ít nhất 2 thẻ từ hợp lệ!');
      return;
    }

    // Auto-fetch phonetics and audio
    setIsFetchingPhonetics(true);
    let cardsWithPhonetics = validCards;
    
    try {
      if (isEditMode && editSetId) {
        // Edit mode: Only fetch for new cards (without originalId)
        const newCards = validCards.filter(c => !c.originalId);
        if (newCards.length > 0) {
          const enrichedNewCards = await fetchPhoneticsForCards(newCards);
          // Merge back with existing cards
          cardsWithPhonetics = validCards.map(c => {
            if (!c.originalId) {
              return enrichedNewCards.find(nc => nc.id === c.id) || c;
            }
            return c;
          });
        }
      } else {
        // Create mode: Fetch for all cards
        cardsWithPhonetics = await fetchPhoneticsForCards(validCards);
      }
    } catch (error) {
      console.error('Error fetching phonetics:', error);
    } finally {
      setIsFetchingPhonetics(false);
    }

    try {
      const setData = {
        folder_id: folderId || undefined,
        name: name.trim(),
        description: description.trim() || undefined,
        language_from: languageFrom,
        language_to: languageTo,
        difficulty: 'easy' as const,
        visibility: 'private' as const,
      };

      let targetSetId = editSetId;

      if (isEditMode && editSetId) {
        // Update set info
        await dispatch(flashcardActions.updateSet(editSetId, setData) as any);
        
        // Get existing cards from store
        const existingSetCards = storeCards.filter((c: any) => c.set_id === editSetId);
        const existingCardIds = new Set(existingSetCards.map((c: any) => c.id));
        
        // Determine which cards to delete (removed from UI)
        const currentOriginalIds = new Set(
          cardList.filter(c => c.originalId).map(c => c.originalId)
        );
        const cardsToDelete = existingSetCards.filter((c: any) => !currentOriginalIds.has(c.id));
        
        // Delete removed cards
        if (cardsToDelete.length > 0) {
          await Promise.all(cardsToDelete.map((c: any) => 
            dispatch(flashcardActions.deleteCard(c.id) as any)
          ));
        }
        
        // Update existing cards
        const cardsToUpdate = cardsWithPhonetics.filter(c => c.originalId);
        if (cardsToUpdate.length > 0) {
          const updateResults = await Promise.allSettled(
            cardsToUpdate.map(async (card) => {
              try {
                await dispatch(flashcardActions.updateCard(card.originalId!, {
                  front: card.front.trim(),
                  back: card.back.trim(),
                  phonetic: card.phonetic?.trim() || undefined,
                  audio_url: card.audio_url?.trim() || undefined,
                  image_url: card.image_url?.trim() || undefined,
                  position: card.position,
                  difficulty: 'medium' as const,
                }) as any);
                return { success: true, card };
              } catch (error: any) {
                console.error(`Failed to update card "${card.front}":`, error);
                return { success: false, card, error };
              }
            })
          );

          // Check for failures and show detailed error
          const failures = updateResults
            .map((result, idx) => ({ result, card: cardsToUpdate[idx] }))
            .filter(({ result }) => result.status === 'rejected' || 
                     (result.status === 'fulfilled' && !result.value.success));

          if (failures.length > 0) {
            const failedCards = failures.map(({ card }) => `"${card.front}"`).join(', ');
            throw new Error(`Không thể cập nhật ${failures.length} thẻ: ${failedCards}. Vui lòng kiểm tra lại URL ảnh hoặc âm thanh.`);
          }
        }
        
        // Create only new cards (without originalId)
        const cardsToCreate = cardsWithPhonetics
          .filter(c => !c.originalId)
          .map(card => ({
            front: card.front.trim(),
            back: card.back.trim(),
            phonetic: card.phonetic?.trim() || undefined,
            audio_url: card.audio_url?.trim() || undefined,
            image_url: card.image_url?.trim() || undefined,
            difficulty: 'medium' as const,
          }));
        
        if (cardsToCreate.length > 0) {
          await dispatch(flashcardActions.bulkCreateCards(editSetId, cardsToCreate) as any);
        }
        
        targetSetId = editSetId;
      } else {
        // Create mode: create set then create all cards
        const newSet: any = await dispatch(flashcardActions.createSet(setData) as any);
        targetSetId = newSet?.id;
        
        if (targetSetId) {
          const cardsToCreate = cardsWithPhonetics.map(card => ({
            front: card.front.trim(),
            back: card.back.trim(),
            phonetic: card.phonetic?.trim() || undefined,
            audio_url: card.audio_url?.trim() || undefined,
            image_url: card.image_url?.trim() || undefined,
            difficulty: 'medium' as const,
          }));

          await dispatch(flashcardActions.bulkCreateCards(targetSetId, cardsToCreate) as any);
        }
      }

      if (targetSetId) {
        // Refresh sets and cards
        dispatch(flashcardActions.getAllSets() as any);
        // Refresh cards for this set so detail view shows immediately
        dispatch(flashcardActions.getCardsBySet(targetSetId) as any);

        if (onSuccess) {
          onSuccess(targetSetId);
        }
      }
    } catch (error) {
      console.error('Error creating/updating set:', error);
    }
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={onBack}>← Quay lại</BackButton>
        <Title>{isEditMode ? 'Chỉnh sửa Set Từ' : 'Tạo Set Từ Mới'}</Title>
      </Header>

      <FormSection>
        <FormGroup>
          <Label>Tiêu đề *</Label>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="VD: IELTS Vocabulary - Unit 1"
          />
        </FormGroup>

        <FormGroup>
          <Label>Mô tả</Label>
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Mô tả về set từ này..."
            rows={2}
          />
        </FormGroup>

        <TwoColumns>
          <FormGroup>
            <Label>Folder (Tuỳ chọn)</Label>
            <Select value={folderId || ''} onChange={(e) => setFolderId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">-- Không chọn folder --</option>
              {folders.map((folder: any) => (
                <option key={folder.id} value={folder.id}>
                  {folder.icon} {folder.name}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Ngôn ngữ</Label>
            <LanguageRow>
              <Input
                type="text"
                value={languageFrom}
                onChange={(e) => setLanguageFrom(e.target.value)}
                placeholder="English"
              />
              <span>→</span>
              <Input
                type="text"
                value={languageTo}
                onChange={(e) => setLanguageTo(e.target.value)}
                placeholder="Vietnamese"
              />
            </LanguageRow>
          </FormGroup>
        </TwoColumns>
      </FormSection>

      <CardsSection>
        <SectionHeader>
          <SectionTitle>📇 Danh sách thẻ từ ({cardList.length})</SectionTitle>
          <ImportButton onClick={() => setShowImportModal(true)}>
            Thêm bằng danh sách có sẵn
          </ImportButton>
        </SectionHeader>
        
        {cardList.map((card, index) => (
          <CardItem
            key={card.id}
            draggable
            onDragStart={() => handleDragStart(card.id)}
            onDragOver={(e) => handleDragOver(e, card.id)}
            onDragEnd={handleDragEnd}
            $isDragging={draggedCard === card.id}
          >
            <CardNumber>{index + 1}</CardNumber>
            <DragHandle>
              <GripVertical size={20} />
            </DragHandle>
            
            <CardContent>
              <CardColumn>
                <CardLabel>Thuật ngữ</CardLabel>
                <CardInput
                  type="text"
                  value={card.front}
                  onChange={(e) => handleCardChange(card.id, 'front', e.target.value)}
                  placeholder="nhập từ vựng..."
                />
                <CardSubRow>
                  <div style={{ flex: '0 0 45%', minWidth: 0 }}>
                    <CardSubLabel>Phát âm</CardSubLabel>
                    <CardSubInput
                      type="text"
                      value={card.phonetic}
                      onChange={(e) => handleCardChange(card.id, 'phonetic', e.target.value)}
                    />
                  </div>
                  <div style={{ flex: '0 0 45%', minWidth: 0 }}>
                    <CardSubLabel>🖼️ Ảnh</CardSubLabel>
                    <ImageUpload
                      value={card.image_url || ''}
                      onChange={(value) => handleCardChange(card.id, 'image_url', value)}
                    />
                  </div>
                </CardSubRow>
              </CardColumn>

              <CardColumn>
                <CardLabel>Định nghĩa</CardLabel>
                <CardTextArea
                  value={card.back}
                  onChange={(e) => handleCardChange(card.id, 'back', e.target.value)}
                  placeholder="VD: Xin chào"
                  rows={3}
                />
              </CardColumn>
            </CardContent>

            <DeleteButton onClick={() => handleRemoveCard(card.id)}>
              <Trash2 size={18} />
            </DeleteButton>
          </CardItem>
        ))}

        <AddCardButton onClick={handleAddCard}>
          <Plus size={20} /> Thêm thẻ
        </AddCardButton>
      </CardsSection>

      <ActionBar>
        <CancelButton onClick={onBack} disabled={isFetchingPhonetics}>Hủy</CancelButton>
        <CreateButton onClick={handleSubmit} disabled={isFetchingPhonetics}>
          {isFetchingPhonetics 
            ? 'Đang lấy phiên âm...' 
            : (isEditMode ? 'Cập nhật bộ từ' : 'Tạo bộ từ')
          }
        </CreateButton>
      </ActionBar>

      <ImportCardsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportCards}
      />
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 0 12px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;

  @media (max-width: 768px) {
    gap: 8px;
    margin-bottom: 16px;
    padding-bottom: 12px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  color: #6b7280;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
    color: #1f2937;
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 14px;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const FormSection = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    padding: 16px;
    margin-bottom: 16px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TwoColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const LanguageRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  span {
    font-size: 18px;
    color: #9ca3af;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 8px;

    span {
      font-size: 16px;
    }
  }
`;

const CardsSection = styled.div`
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const ImportButton = styled.button`
  padding: 10px 16px;
  background: #f0f9ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: #e0f2fe;
    border-color: #7dd3fc;
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 12px;
  }
`;

const CardItem = styled.div<{ $isDragging: boolean }>`
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 12px;
  display: flex;
  gap: 16px;
  align-items: flex-start;
  opacity: ${props => props.$isDragging ? 0.5 : 1};
  cursor: move;
  transition: all 0.2s;

  &:hover {
    border-color: #3b82f6;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  @media (max-width: 768px) {
    padding: 12px;
    gap: 8px;
    flex-wrap: wrap;
  }
`;

const CardNumber = styled.div`
  width: 32px;
  height: 32px;
  background: #3b82f6;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
`;

const DragHandle = styled.div`
  color: #9ca3af;
  cursor: grab;
  padding: 4px;

  &:active {
    cursor: grabbing;
  }
`;

const CardContent = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 16px;
  min-width: 0; /* Prevent grid overflow */

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const CardColumn = styled.div``;

const CardLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 8px;
  text-transform: uppercase;
`;

const CardSubLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  margin-bottom: 4px;
  text-transform: uppercase;
`;

const CardInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  transition: all 0.2s;

  &::placeholder {
    color: #d1d5db;
    font-weight: 400;
    opacity: 0.6;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 8px 10px;
  }
`;

const CardTextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 8px 10px;
  }
`;

const CardSubRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
  flex-wrap: nowrap; /* Prevent wrapping on desktop */

  @media (max-width: 768px) {
    gap: 6px;
    flex-wrap: wrap; /* Allow wrapping on mobile */
  }
`;

const CardSubInput = styled.input`
  flex: 1;
  min-width: 120px;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 12px;
  color: #6b7280;
  background: #f9fafb;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
  }

  @media (max-width: 768px) {
    font-size: 11px;
    padding: 6px 8px;
    min-width: 100px;
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: #fee2e2;
  }
`;

const AddCardButton = styled.button`
  width: 100%;
  padding: 16px;
  background: white;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  color: #6b7280;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    background: #eff6ff;
  }

  @media (max-width: 768px) {
    padding: 12px;
    font-size: 14px;
  }
`;

const ActionBar = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 20px;
  background: white;
  border-radius: 12px;
  border: 2px solid #e5e7eb;

  @media (max-width: 768px) {
    padding: 12px;
    gap: 8px;
    flex-wrap: wrap;
  }
`;

const CancelButton = styled.button`
  padding: 12px 24px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }

  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 14px;
    flex: 1;
  }
`;

const CreateButton = styled.button`
  padding: 12px 32px;
  border: none;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 14px;
    flex: 1;
  }
`;
